import { ReactNode } from 'react';
import { NextSeoProps } from 'next-seo';

import { PageCategory } from '~/features/analytics/constants';
import { StaticDownloadGuideAsideProps } from '~/features/experiments/ab_plp_redesign_2025/components/download-guide-aside';
import { StaticDownloadSyllabusButtonProps } from '~/features/experiments/ab_plp_redesign_2025/components/download-syllabus-button';
import { PageHead } from '~/features/experiments/ab_plp_redesign_2025/components/page-head';
import { StaticPriceCardProps } from '~/features/experiments/ab_plp_redesign_2025/components/price-card';
import { StaticProgramAboutSectionProps } from '~/features/experiments/ab_plp_redesign_2025/components/program-about-section';
import { StaticProgramBreadcrumbsProps } from '~/features/experiments/ab_plp_redesign_2025/components/program-bread-crumbs';
import { StaticProgramDetailsSectionProps } from '~/features/experiments/ab_plp_redesign_2025/components/program-details-section';
import { StaticProgramInstructorsSectionProps } from '~/features/experiments/ab_plp_redesign_2025/components/program-instructors-section';
import {
  PaidPriceCardSlotProps,
  ProgramLandingPageChallenger,
} from '~/features/experiments/ab_plp_redesign_2025/components/program-landing-page';
import { StaticProgramOutlineSectionProps } from '~/features/experiments/ab_plp_redesign_2025/components/program-outline-section';
import { StaticRelatedProgramsSectionProps } from '~/features/experiments/ab_plp_redesign_2025/components/related-programs-section';
import { StaticSalariesSectionProps } from '~/features/experiments/ab_plp_redesign_2025/components/salaries-section';
import { StaticProgramUpsellAsideProps } from '~/features/experiments/ab_plp_redesign_2025/components/upsell-program-aside';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { PageLayout } from '~/features/page/components/page-layout';
import type { Page } from '~/features/page/models/page';
import { useEnrollmentRedirect } from '~/features/program/hooks/use-enrollment-redirect';
import { usePLPViewedEvent } from '~/features/program/hooks/use-plp-viewed-event';
import { StaticOutcomesSubsectionProps } from '~/features/program/project-first-plp/components/outcomes-section';
import { ProjectFirstProgramLandingPage } from '~/features/program/project-first-plp/components/program-landing-page';
import { StaticProjectsSubsectionProps } from '~/features/program/project-first-plp/components/projects-subsection';
import { StaticReviewsSectionProps } from '~/features/program/utils/transform-static-reviews-section-props';
import { escapeJsonLd } from '~/features/seo/utils/seo-price-helpers';
import { useSessionReplay } from '~/features/session-replay';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

export type ExperimentProgramPageDataProps = {
  companySlug?: string;
  difficultyLevel?: string;
  imageUrl: string | null;
  isMastersBannerRendered: boolean;
  isMbaBannerRendered: boolean;
  isProgramFree?: boolean;
  isProjectFirstPlp: boolean;
  page: Page;
  programKey: string;
  semanticType?: string;
  seoProps: NextSeoProps;
  staticBreadcrumbsProps: StaticProgramBreadcrumbsProps;
  staticDownloadGuideAsideProps: StaticDownloadGuideAsideProps;
  staticDownloadSyllabusButtonProps: StaticDownloadSyllabusButtonProps;
  staticOutcomesSubsectionProps: StaticOutcomesSubsectionProps | null;
  staticPriceCardProps: StaticPriceCardProps;
  staticProgramAboutSectionProps: StaticProgramAboutSectionProps;
  staticProgramDetailsSectionProps: StaticProgramDetailsSectionProps;
  staticProgramInstructorsSectionProps: StaticProgramInstructorsSectionProps;
  staticProgramOutlineSectionProps: StaticProgramOutlineSectionProps;
  staticProjectsSubsectionProps: StaticProjectsSubsectionProps | null;
  staticRelatedProgramsSectionProps: StaticRelatedProgramsSectionProps;
  staticReviewsSectionProps: StaticReviewsSectionProps;
  staticSalariesSectionProps: StaticSalariesSectionProps;
  staticUpsellProgramAsideProps: StaticProgramUpsellAsideProps;
  structuredData: Record<string, unknown>;
};

type ExperimentProgramPageProps = ExperimentProgramPageDataProps & {
  catalogImageInPriceCard?: boolean;
  paidPriceCardSlot: (props: PaidPriceCardSlotProps) => ReactNode;
};

export function ExperimentProgramPage({
  page,
  programKey,
  imageUrl,
  structuredData,
  companySlug,
  staticBreadcrumbsProps,
  staticProgramDetailsSectionProps,
  staticProgramOutlineSectionProps,
  staticProgramInstructorsSectionProps,
  staticSalariesSectionProps,
  staticReviewsSectionProps,
  staticProgramAboutSectionProps,
  staticDownloadGuideAsideProps,
  staticDownloadSyllabusButtonProps,
  staticRelatedProgramsSectionProps,
  staticUpsellProgramAsideProps,
  staticProjectsSubsectionProps,
  staticOutcomesSubsectionProps,
  isProjectFirstPlp,
  isProgramFree,
  difficultyLevel,
  staticPriceCardProps,
  seoProps,
  semanticType,
  isMastersBannerRendered,
  isMbaBannerRendered,
  paidPriceCardSlot,
  catalogImageInPriceCard,
}: ExperimentProgramPageProps) {
  usePLPViewedEvent({
    isFree: isProgramFree,
    nodeKey: programKey,
  });

  useSessionReplay();

  useEnrollmentRedirect(programKey);

  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: isProgramFree ? PageCategory.FreeProgram : PageCategory.PaidProgram,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  const sharedProps = {
    catalogImageInPriceCard,
    companySlug,
    imageUrl,
    isMastersBannerRendered,
    isMbaBannerRendered,
    isProgramFree,
    paidPriceCardSlot,
    programKey,
    staticBreadcrumbsProps,
    staticDownloadGuideAsideProps,
    staticDownloadSyllabusButtonProps,
    staticPriceCardProps,
    staticProgramAboutSectionProps,
    staticProgramDetailsSectionProps,
    staticProgramInstructorsSectionProps,
    staticProgramOutlineSectionProps,
    staticRelatedProgramsSectionProps,
    staticReviewsSectionProps,
    staticSalariesSectionProps,
    staticUpsellProgramAsideProps,
  };

  return (
    <>
      <PageHead
        page={page}
        pageCategory={isProgramFree ? PageCategory.FreeProgram : PageCategory.PaidProgram}
        seoProps={seoProps}
        additionalPageAnalytics={{
          degree_key: programKey,
          difficulty: difficultyLevel,
          semanticType,
        }}
      />
      <PageLayout withoutVerticalPadding>
        {isProjectFirstPlp ? (
          <ProjectFirstProgramLandingPage
            {...sharedProps}
            staticOutcomesSubsectionProps={staticOutcomesSubsectionProps}
            staticProjectsSubsectionProps={staticProjectsSubsectionProps}
          />
        ) : (
          <ProgramLandingPageChallenger {...sharedProps} />
        )}
        <script
          dangerouslySetInnerHTML={{ __html: escapeJsonLd(JSON.stringify(structuredData)) }}
          type='application/ld+json'
        />
      </PageLayout>
    </>
  );
}
